import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Loader2, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/api/client';

/**
 * PortalMfaRecoverPage
 *
 * Reached via the "Lost authenticator?" link on the MFA challenge step.
 * The link carries the mfa_session_token as a query param.
 *
 * Flow:
 *   1. Auto-POST /portal/auth/mfa/recover (sends recovery email)
 *   2. User clicks the link in their email → /portal/mfa/recover?token=<recovery_token>
 *   3. Auto-POST /portal/auth/mfa/recover/confirm with that token
 *   4. Receive a short-lived setup JWT → store it, redirect to /portal/mfa/setup
 */
export function PortalMfaRecoverPage() {
  const [searchParams] = useSearchParams();
  const mfaSessionToken = searchParams.get('mfa_session_token') ?? '';
  const recoveryToken = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const called = useRef(false);

  // Step 2: confirm recovery token → get setup JWT
  const confirmMutation = useMutation({
    mutationFn: async (token: string) => {
      const { data } = await apiClient.post('/portal/auth/mfa/recover/confirm', { token });
      if (!data.data) throw new Error(data.errors?.[0]?.message ?? 'Recovery failed');
      return data.data as { setupToken: string };
    },
    onSuccess: (data) => {
      // VMFA-2 / ED-VMFA-1-ENROLLMENT-TARGET-1: route to the working re-enrollment page with the
      // setup token in location.state (NOT the dead /portal/mfa/setup; no auth store involved).
      navigate('/portal/mfa/enrollment', { state: { setupToken: data.setupToken }, replace: true });
    },
    onError: () => {
      toast.error('Recovery link has expired or is invalid');
      navigate('/portal/login', { replace: true });
    },
  });

  // Step 1: send recovery email
  const sendMutation = useMutation({
    mutationFn: async (sessionToken: string) => {
      await apiClient.post('/portal/auth/mfa/recover', {
        mfa_session_token: sessionToken,
      });
    },
    onSuccess: () => {
      // Nothing to do — user will click the link in their email
    },
    onError: () => {
      toast.error('Could not send recovery email. Please sign in and try again.');
      navigate('/portal/login', { replace: true });
    },
  });

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    if (recoveryToken) {
      // User has arrived via the emailed recovery link
      confirmMutation.mutate(recoveryToken);
    } else if (mfaSessionToken) {
      // User clicked "Lost authenticator?" — send the recovery email
      sendMutation.mutate(mfaSessionToken);
    } else {
      navigate('/portal/login', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isConfirming = !!recoveryToken;

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-10 w-auto" />
          <div className="text-center">
            <h1 className="text-xl font-bold text-text-primary">Vendor Security Portal</h1>
            <p className="mt-0.5 text-sm text-text-secondary">Powered by VeriGRC</p>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-2xl">
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            {isConfirming ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-accent-cyan" />
                <p className="text-base font-medium text-text-primary">Verifying recovery link…</p>
                <p className="text-sm text-text-secondary">You'll be redirected to set up a new authenticator.</p>
              </>
            ) : (
              <>
                <ShieldOff className="h-8 w-8 text-accent-amber" />
                <p className="text-base font-medium text-text-primary">Recovery email sent</p>
                <p className="text-sm text-text-secondary">
                  Check your inbox for a link to reset your two-factor authentication.
                  The link expires in 15 minutes.
                </p>
                <p className="text-xs text-text-secondary">
                  You can close this tab and click the link from your email.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
